import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import useAdminIdeas from "@/pages/admin/ideas/useAdminIdeas";
import { clearCache } from "@/pages/admin/adminCache";

const KEY_STORAGE = "vbb_admin_key";

const blankForm = () => ({
  title: "",
  kind: "either",
  summary: "",
  notes: "",
  tags: [],
  status: "backlog",
  source: "",
  related_ideas: [],
});

/**
 * /admin/ideas/new + /admin/ideas/:id — single form for create+edit.
 * "Promote to essay" button (edit mode only) creates a draft essay
 * pre-populated with this idea's contents and links back via essay_id.
 */
export default function AdminIdeaEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const {
    key,
    ideas,
    createIdea,
    getIdea,
    patchIdea,
    promoteToEssay,
    findSimilar,
    mergeInto,
    unmerge,
  } = useAdminIdeas();

  const [loaded, setLoaded] = useState(isNew);
  const [savedState, setSavedState] = useState("idle");
  const [form, setForm] = useState(blankForm);
  const [tagInput, setTagInput] = useState("");
  const [essayId, setEssayId] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [similar, setSimilar] = useState(null); // null=not fetched, []=fetched
  const [similarLoading, setSimilarLoading] = useState(false);
  const [mergingId, setMergingId] = useState(null);
  const [mergedIntoId, setMergedIntoId] = useState(null);
  const [unmerging, setUnmerging] = useState(false);

  useEffect(() => {
    if (!key) {
      navigate("/login?next=/admin/ideas", { replace: true });
      return;
    }
    if (isNew) return;
    let cancelled = false;
    getIdea(id)
      .then((data) => {
        if (cancelled) return;
        setForm({
          title: data.title || "",
          kind: data.kind || "either",
          summary: data.summary || "",
          notes: data.notes || "",
          tags: data.tags || [],
          status: data.status || "backlog",
          source: data.source || "",
          related_ideas: data.related_ideas || [],
        });
        setEssayId(data.essay_id || null);
        setMergedIntoId(data.merged_into_id || null);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSavedState("error");
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, key, getIdea, navigate]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    navigate("/login?next=/admin/ideas", { replace: true });
  };

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag || form.tags.includes(tag)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, tag] }));
    setTagInput("");
  };

  const removeTag = (tag) =>
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));

  const onSave = async () => {
    if (!form.title.trim()) {
      setSavedState("error");
      return;
    }
    setSavedState("saving");
    try {
      if (isNew) {
        const created = await createIdea(form);
        setSavedState("saved");
        navigate(`/admin/ideas/${created.id}`, { replace: true });
        return;
      }
      await patchIdea(id, form);
      setSavedState("saved");
      setTimeout(() => setSavedState("idle"), 1800);
    } catch {
      setSavedState("error");
    }
  };

  const onPromote = async () => {
    if (!id || isNew) return;
    if (
      !window.confirm(
        "Create a draft essay from this idea and mark this idea as drafting?"
      )
    )
      return;
    setPromoting(true);
    try {
      const essay = await promoteToEssay(id);
      navigate(`/admin/essays/${essay.id}`);
    } catch {
      setPromoting(false);
      setSavedState("error");
    }
  };

  const onFindSimilar = async () => {
    if (!id || isNew) return;
    setSimilarLoading(true);
    try {
      const results = await findSimilar(id, 3);
      setSimilar(results || []);
    } catch {
      setSimilar([]);
    } finally {
      setSimilarLoading(false);
    }
  };

  const linkAsRelated = (otherId) => {
    if (form.related_ideas.includes(otherId)) return;
    setForm((f) => ({ ...f, related_ideas: [...f.related_ideas, otherId] }));
  };

  const onMergeIntoTarget = async (targetId, targetTitle) => {
    if (!id || isNew) return;
    if (
      !window.confirm(
        `Merge THIS idea into "${targetTitle}"?\n\nThis idea's notes will be appended to the target and this idea will be marked 'merged'.`
      )
    )
      return;
    setMergingId(targetId);
    try {
      await mergeInto(id, targetId);
      // Redirect the user to the target so they can review the merged notes.
      navigate(`/admin/ideas/${targetId}`);
    } catch {
      setMergingId(null);
      setSavedState("error");
    }
  };

  const onUnmerge = async () => {
    if (!id || isNew) return;
    if (
      !window.confirm(
        "Undo the merge?\n\nThis idea will flip back to 'backlog' and the link to the merged-into idea will be cleared.\n\nNote: notes appended to the other idea are NOT removed automatically — you can trim them by hand if you want."
      )
    )
      return;
    setUnmerging(true);
    try {
      const updated = await unmerge(id);
      setForm((f) => ({ ...f, status: updated.status || "backlog" }));
      setMergedIntoId(null);
      setSavedState("saved");
      setTimeout(() => setSavedState("idle"), 1800);
    } catch {
      setSavedState("error");
    } finally {
      setUnmerging(false);
    }
  };

  if (!key || !loaded) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-idea-edit-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            /{" "}
            <Link
              to="/admin/ideas"
              className="vbb-link"
              data-testid="crumb-ideas"
            >
              Ideas
            </Link>{" "}
            / {isNew ? "New" : "Edit"}
          </div>
          <h1
            className="font-display uppercase text-4xl sm:text-5xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-idea-edit-title"
          >
            <BoxedTitle size="lg" thick>
              {isNew ? "New" : "Edit"}
            </BoxedTitle>{" "}
            idea.
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/ideas"
            className="vbb-btn vbb-btn-ghost"
            data-testid="idea-back-btn"
          >
            ← Ideas
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <div>
            <label htmlFor="idea-title" className="vbb-overline block mb-2">
              Title *
            </label>
            <input
              id="idea-title"
              type="text"
              value={form.title}
              onChange={(e) => setField({ title: e.target.value })}
              className="w-full border border-[#0a0a0a] p-3 text-[18px] focus:outline-none focus:border-[#e34234]"
              data-testid="idea-title-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="idea-kind" className="vbb-overline block mb-2">
                Kind
              </label>
              <select
                id="idea-kind"
                value={form.kind}
                onChange={(e) => setField({ kind: e.target.value })}
                className="w-full border border-[#0a0a0a] p-3 text-[14px] bg-white focus:outline-none focus:border-[#e34234]"
                data-testid="idea-kind-select"
              >
                <option value="story">Story</option>
                <option value="essay">Essay</option>
                <option value="either">Either / undecided</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="idea-status"
                className="vbb-overline block mb-2"
              >
                Status
              </label>
              <select
                id="idea-status"
                value={form.status}
                onChange={(e) => setField({ status: e.target.value })}
                className="w-full border border-[#0a0a0a] p-3 text-[14px] bg-white focus:outline-none focus:border-[#e34234]"
                data-testid="idea-status-select"
              >
                <option value="backlog">Backlog</option>
                <option value="selected">Selected — up next</option>
                <option value="drafting">Drafting</option>
                <option value="shelved">Shelved</option>
                <option value="merged">Merged into another idea</option>
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor="idea-summary"
              className="vbb-overline block mb-2"
            >
              Summary (1-2 sentences — the elevator pitch to yourself)
            </label>
            <textarea
              id="idea-summary"
              value={form.summary}
              onChange={(e) => setField({ summary: e.target.value })}
              rows={3}
              className="w-full border border-[#0a0a0a] p-3 text-[14px] resize-y focus:outline-none focus:border-[#e34234]"
              data-testid="idea-summary-input"
            />
          </div>

          <div>
            <label htmlFor="idea-notes" className="vbb-overline block mb-2">
              Notes (markdown — flesh it out here)
            </label>
            <textarea
              id="idea-notes"
              value={form.notes}
              onChange={(e) => setField({ notes: e.target.value })}
              rows={12}
              className="w-full border border-[#0a0a0a] p-4 font-mono-tiny text-[13px] resize-y focus:outline-none focus:border-[#e34234]"
              placeholder="Scenes, characters, arguments, sources to check, questions you're chasing…"
              data-testid="idea-notes-input"
            />
          </div>

          <div>
            <label className="vbb-overline block mb-2">Tags</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add a tag and press Enter"
                className="flex-1 border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                data-testid="idea-tag-input"
              />
              <button
                type="button"
                onClick={addTag}
                className="vbb-btn vbb-btn-ghost"
                data-testid="idea-tag-add-btn"
              >
                + Add
              </button>
            </div>
            {form.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {form.tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => removeTag(t)}
                    className="font-mono-tiny text-[11px] px-2 py-1 border border-[#0a0a0a] hover:bg-[#e34234] hover:text-white hover:border-[#e34234]"
                    data-testid={`idea-tag-${t}`}
                  >
                    {t} ×
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="idea-source" className="vbb-overline block mb-2">
              Source (which file / list this came from — optional)
            </label>
            <input
              id="idea-source"
              type="text"
              value={form.source}
              onChange={(e) => setField({ source: e.target.value })}
              placeholder="e.g. 'Ideas List 2 — 2024'"
              className="w-full border border-[#0a0a0a] p-2 text-[14px] font-mono-tiny focus:outline-none focus:border-[#e34234]"
              data-testid="idea-source-input"
            />
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          {!isNew && form.status === "merged" && (
            <section
              className="border border-[#6b6b6b] p-5 bg-[#fafafa]"
              data-testid="idea-merged-panel"
            >
              <div className="vbb-overline text-[#6b6b6b] mb-2">
                Merged idea
              </div>
              <p className="text-[13px] leading-relaxed text-[#2a2a2a]">
                This idea was merged into another one.
                {mergedIntoId && (
                  <>
                    {" "}
                    <Link
                      to={`/admin/ideas/${mergedIntoId}`}
                      className="vbb-link"
                      data-testid="idea-merged-target-link"
                    >
                      Open the target idea →
                    </Link>
                  </>
                )}
              </p>
              <button
                type="button"
                onClick={onUnmerge}
                disabled={unmerging}
                className="mt-4 vbb-btn vbb-btn-ghost w-full disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="idea-unmerge-btn"
              >
                {unmerging ? "Unmerging…" : "Undo merge → back to backlog"}
              </button>
              <p className="mt-3 font-mono-tiny text-[11px] text-[#6b6b6b]">
                Note: appended notes on the target idea are not
                automatically removed — trim by hand if needed.
              </p>
            </section>
          )}

          <section
            className="border border-[#0a0a0a] p-5"
            data-testid="idea-save-panel"
          >
            <div className="vbb-overline mb-3">Save</div>
            <div className="flex items-center justify-between">
              <span
                className="font-mono-tiny text-[#6b6b6b]"
                data-testid="idea-save-state"
              >
                {savedState === "saving" && "Saving…"}
                {savedState === "saved" && "Saved."}
                {savedState === "error" && (
                  <span className="text-[#e34234]">Save failed.</span>
                )}
              </span>
              <button
                type="button"
                onClick={onSave}
                disabled={savedState === "saving" || !form.title.trim()}
                className="vbb-btn disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="idea-save-btn"
              >
                Save
              </button>
            </div>
          </section>

          {!isNew && (
            <section
              className="border border-[#e34234] p-5 bg-white"
              data-testid="idea-promote-panel"
            >
              <div className="vbb-overline text-[#e34234] mb-3">
                Ready to work on this?
              </div>
              {essayId ? (
                <div className="space-y-3">
                  <p className="text-[14px] text-[#2a2a2a]">
                    This idea is already linked to a draft essay.
                  </p>
                  <Link
                    to={`/admin/essays/${essayId}`}
                    className="vbb-btn w-full text-center"
                    data-testid="idea-open-essay-btn"
                  >
                    Open the draft →
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[14px] text-[#2a2a2a]">
                    Promote this idea to a new draft essay. Copies the
                    summary and notes into the essay body and sets this
                    idea&apos;s status to <em>drafting</em>.
                  </p>
                  <button
                    type="button"
                    onClick={onPromote}
                    disabled={promoting || !form.title.trim()}
                    className="vbb-btn w-full disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="idea-promote-btn"
                  >
                    {promoting ? "Promoting…" : "Promote to essay →"}
                  </button>
                </div>
              )}
            </section>
          )}

          {!isNew && (
            <section
              className="border border-[#0a0a0a] p-5"
              data-testid="idea-find-similar-panel"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="vbb-overline">Find similar</div>
                <button
                  type="button"
                  onClick={onFindSimilar}
                  disabled={similarLoading}
                  className="vbb-btn vbb-btn-ghost text-[12px] px-3 py-1 disabled:opacity-40"
                  data-testid="idea-find-similar-btn"
                >
                  {similarLoading
                    ? "Searching…"
                    : similar
                      ? "Refresh"
                      : "Find top 3"}
                </button>
              </div>
              <p className="text-[12px] text-[#6b6b6b] mb-3">
                Fuzzy match on title + summary + notes. Use to catch
                duplicates before promoting.
              </p>
              {similar && similar.length === 0 && !similarLoading && (
                <p
                  className="text-[13px] text-[#6b6b6b] italic"
                  data-testid="idea-similar-empty"
                >
                  No candidates found.
                </p>
              )}
              {similar && similar.length > 0 && (
                <ul className="space-y-3" data-testid="idea-similar-results">
                  {similar.map((row) => {
                    const other = row.idea;
                    const linked = form.related_ideas.includes(other.id);
                    return (
                      <li
                        key={other.id}
                        className="border border-[#c9c9c9] p-3"
                        data-testid={`idea-similar-row-${other.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-mono-tiny text-[11px] text-[#6b6b6b] uppercase tracking-wider">
                              score {(row.score * 100).toFixed(0)}%
                            </div>
                            <Link
                              to={`/admin/ideas/${other.id}`}
                              className="text-[13px] leading-snug hover:text-[#e34234]"
                              data-testid={`idea-similar-link-${other.id}`}
                            >
                              {other.title}
                            </Link>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => linkAsRelated(other.id)}
                            disabled={linked}
                            className="font-mono-tiny text-[11px] px-2 py-1 border border-[#0a0a0a] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0a0a0a] hover:text-white"
                            data-testid={`idea-similar-link-btn-${other.id}`}
                          >
                            {linked ? "✓ Linked" : "Link as related"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              onMergeIntoTarget(other.id, other.title)
                            }
                            disabled={mergingId !== null}
                            className="font-mono-tiny text-[11px] px-2 py-1 border border-[#e34234] text-[#e34234] hover:bg-[#e34234] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`idea-similar-merge-btn-${other.id}`}
                          >
                            {mergingId === other.id
                              ? "Merging…"
                              : "Merge in →"}
                          </button>
                          <Link
                            to={`/admin/ideas/${other.id}`}
                            className="font-mono-tiny text-[11px] px-2 py-1 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
                            data-testid={`idea-similar-open-btn-${other.id}`}
                          >
                            Open →
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {!isNew && form.related_ideas.length > 0 && (
            <section
              className="border border-[#0a0a0a] p-5"
              data-testid="idea-related-panel"
            >
              <div className="vbb-overline mb-3">
                Related ideas ({form.related_ideas.length})
              </div>
              <ul className="space-y-2" data-testid="idea-related-list">
                {form.related_ideas.map((rid) => {
                  const other = ideas.find((i) => i.id === rid);
                  if (!other) return null;
                  return (
                    <li
                      key={rid}
                      className="flex items-start gap-2"
                      data-testid={`idea-related-row-${rid}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            related_ideas: f.related_ideas.filter(
                              (x) => x !== rid
                            ),
                          }))
                        }
                        className="font-mono-tiny text-[11px] px-1.5 border border-[#e34234] text-[#e34234] hover:bg-[#e34234] hover:text-white shrink-0 mt-0.5"
                        data-testid={`idea-related-unlink-btn-${rid}`}
                        title="Unlink"
                      >
                        ×
                      </button>
                      <Link
                        to={`/admin/ideas/${rid}`}
                        className="text-[13px] leading-snug hover:text-[#e34234]"
                      >
                        {other.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </aside>
      </div>
    </main>
  );
}
